import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import AdUnit from '../components/AdUnit';
import SEO from '../components/SEO';
import TextareaWithLines from '../components/TextareaWithLines';
import PreWithLines from '../components/PreWithLines';

const JwtDecoderPage = () => {
  const [activeTab, setActiveTab] = useState('decoder');
  const [jwtToken, setJwtToken] = useState('');
  const [decodedHeader, setDecodedHeader] = useState('');
  const [decodedPayload, setDecodedPayload] = useState('');
  const [signature, setSignature] = useState('');
  const [secret, setSecret] = useState('your-256-bit-secret');
  const [publicKey, setPublicKey] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [algorithm, setAlgorithm] = useState('HS256');
  const [isValid, setIsValid] = useState(null);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState('');
  const [headerMaximized, setHeaderMaximized] = useState(false);
  const [payloadMaximized, setPayloadMaximized] = useState(false);
  const [signatureMaximized, setSignatureMaximized] = useState(false);
  const [showHeaderClaims, setShowHeaderClaims] = useState(false);
  const [showPayloadClaims, setShowPayloadClaims] = useState(false);
  const [payloadDisplayMode, setPayloadDisplayMode] = useState('raw'); // 'raw', 'unescaped', 'claims'

  // Encoder tab states
  const [encoderHeaderJson, setEncoderHeaderJson] = useState('{\n  "alg": "HS256",\n  "typ": "JWT"\n}');
  const [encoderPayloadJson, setEncoderPayloadJson] = useState('{\n  "sub": "1234567890",\n  "name": "John Doe",\n  "iat": 1516239022\n}');
  const [encoderSecret, setEncoderSecret] = useState('your-256-bit-secret');
  const [encoderAlgorithm, setEncoderAlgorithm] = useState('HS256');
  const [encodedJwtToken, setEncodedJwtToken] = useState('');
  const [encoderError, setEncoderError] = useState('');
  const [editableToken, setEditableToken] = useState('');

  // Refs for scroll syncing
  const inputRef = useRef(null);
  const headerRef = useRef(null);
  const payloadRef = useRef(null);

  // Sample JWT token for demonstration
  useEffect(() => {
    const sampleToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c';
    setJwtToken(sampleToken);
  }, []);

  // Base64URL decode function
  const base64UrlDecode = (str) => {
    try {
      // Replace URL-safe characters
      let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
      // Add padding if needed
      while (base64.length % 4) {
        base64 += '=';
      }
      
      // Handle special characters that might cause atob() to fail
      try {
        return atob(base64);
      } catch (atobError) {
        // If atob fails, try manual decoding for problematic characters
        // This handles cases where the base64 string contains characters that atob can't handle
        try {
          // Use Buffer for more robust base64 decoding (Node.js style in browser)
          if (typeof Buffer !== 'undefined') {
            return Buffer.from(base64, 'base64').toString('utf8');
          } else {
            // Fallback: try to clean the string and retry atob
            const cleaned = base64.replace(/[^A-Za-z0-9+/=]/g, '');
            return atob(cleaned);
          }
        } catch (fallbackError) {
          // If all methods fail, throw the original error with more context
          throw new Error(`Invalid base64 encoding: ${atobError.message}. Original string: ${str.substring(0, 50)}${str.length > 50 ? '...' : ''}`);
        }
      }
    } catch (e) {
      throw new Error(`Base64 decode error: ${e.message}`);
    }
  };

  // Base64URL encode function
  const base64UrlEncode = (str) => {
    return btoa(str)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };

  // Convert byte array directly to Base64URL (avoids double-encoding signatures)
  const uint8ArrayToBase64Url = (uint8Array) => {
    const binary = String.fromCharCode(...uint8Array);
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };

  // Simple HMAC SHA256 implementation (for demonstration - in production use crypto library)
  const hmacSha256 = async (message, secret) => {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(secret);
    const messageData = encoder.encode(message);
    
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    
    const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData);
    return new Uint8Array(signature);
  };

  // Decode JWT token
  const decodeJWT = (token) => {
    try {
      setError('');
      
      // Clean the token: remove all whitespace, line breaks, and tabs
      const cleanedToken = token.replace(/\s+/g, '').trim();
      
      if (!cleanedToken) {
        setDecodedHeader('');
        setDecodedPayload('');
        setSignature('');
        setIsValid(null);
        return;
      }

      const parts = cleanedToken.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format. JWT should have 3 parts separated by dots.');
      }

      const [headerPart, payloadPart, signaturePart] = parts;

      // Decode header
      const headerDecoded = base64UrlDecode(headerPart);
      const headerObj = JSON.parse(headerDecoded);
      setDecodedHeader(JSON.stringify(headerObj, null, 2));

      // Decode payload
      const payloadDecoded = base64UrlDecode(payloadPart);
      const payloadObj = JSON.parse(payloadDecoded);
      setDecodedPayload(JSON.stringify(payloadObj, null, 2));

      // Set signature
      setSignature(signaturePart);

      // Verify signature if secret is provided
      verifySignature(cleanedToken, headerObj.alg || 'HS256');

    } catch (e) {
      setError('Error decoding JWT: ' + e.message);
      setDecodedHeader('');
      setDecodedPayload('');
      setSignature('');
      setIsValid(false);
    }
  };

  // Verify JWT signature
  const verifySignature = useCallback(async (token, alg) => {
    try {
      if (alg === 'HS256' && secret.trim()) {
        const parts = token.split('.');
        const header = parts[0];
        const payload = parts[1];
        const providedSignature = parts[2];

        // Create the signature base
        const signatureBase = `${header}.${payload}`;
        
        // Generate expected signature
        const expectedSignatureArray = await hmacSha256(signatureBase, secret);
        const expectedSignature = uint8ArrayToBase64Url(expectedSignatureArray);

        setIsValid(expectedSignature === providedSignature);
      } else if (alg.startsWith('RS') && publicKey.trim()) {
        // RSA verification would need a crypto library
        setIsValid(null);
        setError('RSA signature verification requires additional crypto library');
      } else {
        setIsValid(null);
      }
    } catch (e) {
      console.error('Signature verification error:', e);
      setIsValid(false);
    }
  }, [secret, publicKey]);

  // Generate new JWT token
  const generateJWT = useCallback(() => {
    try {
      const header = {
        alg: algorithm,
        typ: 'JWT'
      };

      const payload = {
        sub: '1234567890',
        name: 'John Doe',
        iat: Math.floor(Date.now() / 1000),
        exp: Math.floor(Date.now() / 1000) + (60 * 60) // 1 hour expiration
      };

      const headerEncoded = base64UrlEncode(JSON.stringify(header));
      const payloadEncoded = base64UrlEncode(JSON.stringify(payload));
      
      // For demonstration, create a simple signature
      const signatureBase = `${headerEncoded}.${payloadEncoded}`;
      
      if (algorithm === 'HS256') {
        hmacSha256(signatureBase, secret).then(signatureArray => {
          const signatureEncoded = uint8ArrayToBase64Url(signatureArray);
          const newToken = `${headerEncoded}.${payloadEncoded}.${signatureEncoded}`;
          setJwtToken(newToken);
        });
      }
    } catch (e) {
      setError('Error generating JWT: ' + e.message);
    }
  }, [algorithm, secret]);

  // Generate example token
  const generateExample = useCallback(() => {
    const exampleToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c';
    setJwtToken(exampleToken);
  }, []);

  // Get claims from decoded JSON
  const getClaims = useMemo(() => {
    return (jsonString) => {
      if (!jsonString) return [];
      try {
        const obj = JSON.parse(jsonString);
        return Object.entries(obj).map(([key, value]) => ({ key, value, type: typeof value }));
      } catch {
        return [];
      }
    };
  }, []);

  const headerClaims = useMemo(() => getClaims(decodedHeader), [decodedHeader, getClaims]);
  const payloadClaims = useMemo(() => getClaims(decodedPayload), [decodedPayload, getClaims]);

  // JSON unescape function
  const unescapeJsonString = (jsonString) => {
    if (!jsonString) return '';
    try {
      const obj = JSON.parse(jsonString);
      
      // Function to recursively unescape JSON strings in object values
      const unescapeNestedJson = (obj) => {
        if (typeof obj === 'string') {
          try {
            // Try to parse as JSON string
            const parsed = JSON.parse(obj);
            return unescapeNestedJson(parsed); // Recursively unescape
          } catch {
            // If not valid JSON, return as is
            return obj;
          }
        } else if (Array.isArray(obj)) {
          return obj.map(unescapeNestedJson);
        } else if (obj && typeof obj === 'object') {
          const result = {};
          for (const [key, value] of Object.entries(obj)) {
            result[key] = unescapeNestedJson(value);
          }
          return result;
        }
        return obj;
      };

      const unescapedObj = unescapeNestedJson(obj);
      return JSON.stringify(unescapedObj, null, 2);
    } catch (e) {
      return jsonString; // Return original if parsing fails
    }
  };

  const unescapedPayload = useMemo(() => unescapeJsonString(decodedPayload), [decodedPayload]);

  // Parse edited token back to header/payload JSON
  const parseEditedToken = useCallback(() => {
    try {
      setEncoderError('');
      
      // Clean the token
      const cleanedToken = editableToken.replace(/\s+/g, '').trim();
      
      if (!cleanedToken) {
        setEncoderError('Please enter a JWT token to parse');
        return;
      }

      const parts = cleanedToken.split('.');
      if (parts.length !== 3) {
        setEncoderError('Invalid JWT format. JWT should have 3 parts separated by dots.');
        return;
      }

      const [headerPart, payloadPart] = parts;

      // Decode header
      const headerDecoded = base64UrlDecode(headerPart);
      const headerObj = JSON.parse(headerDecoded);
      setEncoderHeaderJson(JSON.stringify(headerObj, null, 2));
      setEncoderAlgorithm(headerObj.alg || 'HS256');

      // Decode payload
      const payloadDecoded = base64UrlDecode(payloadPart);
      const payloadObj = JSON.parse(payloadDecoded);
      setEncoderPayloadJson(JSON.stringify(payloadObj, null, 2));

      // Update the main encoded token
      setEncodedJwtToken(cleanedToken);

    } catch (e) {
      setEncoderError('Error parsing JWT: ' + e.message);
    }
  }, [editableToken]);

  // Encoder functions
  const encodeJWT = useCallback(async () => {
    try {
      setEncoderError('');
      
      // Parse header and payload JSON
      const headerObj = JSON.parse(encoderHeaderJson);
      const payloadObj = JSON.parse(encoderPayloadJson);
      
      // Update algorithm in header
      headerObj.alg = encoderAlgorithm;
      
      // Encode header and payload
      const headerEncoded = base64UrlEncode(JSON.stringify(headerObj));
      const payloadEncoded = base64UrlEncode(JSON.stringify(payloadObj));
      
      // Create signature base
      const signatureBase = `${headerEncoded}.${payloadEncoded}`;
      
      if (encoderAlgorithm === 'HS256' && encoderSecret.trim()) {
        const signatureArray = await hmacSha256(signatureBase, encoderSecret);
        const signatureEncoded = uint8ArrayToBase64Url(signatureArray);
        const newToken = `${headerEncoded}.${payloadEncoded}.${signatureEncoded}`;
        setEncodedJwtToken(newToken);
        setEditableToken(newToken);
      } else {
        // For other algorithms, just create unsigned token
        const unsignedToken = `${headerEncoded}.${payloadEncoded}.`;
        setEncodedJwtToken(unsignedToken);
        setEditableToken(unsignedToken);
        setEncoderError('Only HS256 signing is implemented in this demo');
      }
    } catch (e) {
      setEncoderError('Error encoding JWT: ' + e.message);
      setEncodedJwtToken('');
    }
  }, [encoderHeaderJson, encoderPayloadJson, encoderSecret, encoderAlgorithm]);

  // Auto-encode when inputs change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      encodeJWT();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [encodeJWT]);

  // Auto-parse when editable token changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (editableToken && editableToken !== encodedJwtToken) {
        parseEditedToken();
      }
    }, 500); // Slightly longer delay to avoid too frequent parsing
    return () => clearTimeout(timeoutId);
  }, [editableToken, encodedJwtToken, parseEditedToken]);

  // Copy to clipboard
  const copyToClipboard = (text, type) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(type);
      setTimeout(() => setCopySuccess(''), 1500);
    });
  };

  // Update decoded content when token changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      decodeJWT(jwtToken);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [jwtToken, secret, algorithm]);

  // Get token parts with colors
  const getColoredToken = () => {
    if (!jwtToken || error) return jwtToken;
    
    const parts = jwtToken.split('.');
    if (parts.length !== 3) return jwtToken;

    return (
      <span>
        <span style={{ color: '#fb015b' }}>{parts[0]}</span>
        <span style={{ color: '#d63aff' }}>.</span>
        <span style={{ color: '#00b9f1' }}>{parts[1]}</span>
        <span style={{ color: '#d63aff' }}>.</span>
        <span style={{ color: '#00d9ff' }}>{parts[2]}</span>
      </span>
    );
  };

  const controlButtonStyle = {
    padding: '8px 16px',
    margin: '5px',
    backgroundColor: '#00BFA5',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  };

  const sectionStyle = {
    backgroundColor: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: '8px',
    padding: '20px',
    margin: '10px 0'
  };

  const tabStyle = {
    padding: '15px 30px',
    border: '2px solid #e9ecef',
    backgroundColor: '#f8f9fa',
    cursor: 'pointer',
    borderRadius: '8px 8px 0 0',
    fontSize: '18px',
    fontWeight: '600',
    color: '#6c757d',
    marginRight: '5px',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transform: 'translateY(2px)'
  };

  const activeTabStyle = {
    ...tabStyle,
    backgroundColor: '#fff',
    color: '#00BFA5',
    borderColor: '#00BFA5',
    borderBottom: '2px solid #fff',
    transform: 'translateY(0px)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    zIndex: 10,
    position: 'relative'
  };

  const ClaimsTable = ({ claims, title }) => (
    <div style={{ marginTop: '10px' }}>
      <h4 style={{ fontSize: '14px', margin: '0 0 10px 0', color: '#666' }}>{title}</h4>
      <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#f0f0f0' }}>
            <th style={{ padding: '6px', textAlign: 'left', border: '1px solid #ddd' }}>Claim</th>
            <th style={{ padding: '6px', textAlign: 'left', border: '1px solid #ddd' }}>Value</th>
            <th style={{ padding: '6px', textAlign: 'left', border: '1px solid #ddd' }}>Type</th>
          </tr>
        </thead>
        <tbody>
          {claims.map((claim, index) => (
            <tr key={index}>
              <td style={{ padding: '6px', border: '1px solid #ddd', fontFamily: 'monospace' }}>{claim.key}</td>
              <td style={{ padding: '6px', border: '1px solid #ddd', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {typeof claim.value === 'object' ? JSON.stringify(claim.value) : String(claim.value)}
              </td>
              <td style={{ padding: '6px', border: '1px solid #ddd', fontSize: '11px', color: '#666' }}>{claim.type}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <SEO 
        title="JWT Decoder - JSON Web Token Debugger & Encoder"
        description="Decode, verify, and generate JSON Web Tokens (JWT). Debug JWT tokens, validate signatures, and encode new tokens with custom claims."
        keywords={["JWT decoder", "JSON Web Token", "JWT debugger", "JWT encoder", "JWT validator", "token decoder", "JWT signature verification"]}
        canonicalUrl="https://yourjsontools.com/jwt-decoder"
      />
      
      <div style={{ maxWidth: '1600px', margin: '0 auto', padding: '20px' }}>
        <h1 style={{ textAlign: 'center', fontSize: '2.5rem', marginBottom: '10px', color: '#333' }}>
          🔐 JWT Decoder & Encoder
        </h1>
        <p style={{ textAlign: 'center', color: '#666', marginBottom: '20px', fontSize: '18px' }}>
          Decode, verify, and generate JSON Web Tokens (JWT). Debug JWT tokens, validate signatures, and encode new tokens with custom claims.
        </p>

        {/* Tab Navigation */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '0px', 
          borderBottom: '2px solid #e9ecef',
          paddingBottom: '0px',
          position: 'relative'
        }}>
          <div style={{ display: 'inline-flex', gap: '0px' }}>
            <button 
              style={activeTab === 'decoder' ? activeTabStyle : tabStyle}
              onClick={() => setActiveTab('decoder')}
              onMouseEnter={(e) => {
                if (activeTab !== 'decoder') {
                  e.target.style.backgroundColor = '#e9ecef';
                  e.target.style.color = '#495057';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'decoder') {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.color = '#6c757d';
                }
              }}
            >
              🔍 JWT Debugger
            </button>
            <button 
              style={activeTab === 'encoder' ? activeTabStyle : tabStyle}
              onClick={() => setActiveTab('encoder')}
              onMouseEnter={(e) => {
                if (activeTab !== 'encoder') {
                  e.target.style.backgroundColor = '#e9ecef';
                  e.target.style.color = '#495057';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'encoder') {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.color = '#6c757d';
                }
              }}
            >
              ⚙️ JWT Encoder
            </button>
          </div>
        </div>

        {activeTab === 'decoder' && (
        <div style={{ 
          display: 'flex', 
          gap: '20px', 
          minHeight: '600px',
          backgroundColor: '#fff',
          border: '2px solid #00BFA5',
          borderTop: 'none',
          borderRadius: '0 0 12px 12px',
          padding: '20px',
          boxShadow: '0 4px 12px rgba(0,191,165,0.1)'
        }}>
          {/* Left Panel - Encoded JWT */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={sectionStyle}>
              <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>Encoded JWT Token</h3>
              
              <div style={{ marginBottom: '15px' }}>
                <textarea
                  value={jwtToken}
                  onChange={(e) => setJwtToken(e.target.value)}
                  onBlur={(e) => {
                    // Clean the token when user finishes editing (on blur)
                    const cleaned = e.target.value.replace(/\s+/g, '').trim();
                    if (cleaned !== e.target.value) {
                      setJwtToken(cleaned);
                    }
                  }}
                  placeholder="Paste your JWT token here..."
                  style={{
                    width: '100%',
                    height: '120px',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: '12px',
                    lineHeight: '1.4',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {jwtToken && !error && (
                <div style={{ 
                  padding: '10px', 
                  backgroundColor: '#fff', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  fontFamily: 'Monaco, Consolas, monospace',
                  fontSize: '12px',
                  wordBreak: 'break-all',
                  lineHeight: '1.6'
                }}>
                  {getColoredToken()}
                </div>
              )}

              {error && (
                <div style={{ 
                  padding: '10px', 
                  backgroundColor: '#f8d7da', 
                  color: '#721c24',
                  border: '1px solid #f5c6cb',
                  borderRadius: '4px',
                  marginTop: '10px'
                }}>
                  {error}
                </div>
              )}

              <div style={{ marginBottom: '15px' }}>
                <button onClick={generateExample} style={{...controlButtonStyle, backgroundColor: '#fd7e14'}}>
                  Example
                </button>
                <button onClick={generateJWT} style={controlButtonStyle}>
                  Generate JWT
                </button>
                <button 
                  onClick={() => copyToClipboard(jwtToken, 'token')} 
                  style={{...controlButtonStyle, backgroundColor: copySuccess === 'token' ? '#4CAF50' : '#6c757d'}}
                >
                  {copySuccess === 'token' ? 'Copied!' : 'Copy Token'}
                </button>
              </div>
            </div>

            {/* Algorithm & Secret */}
            <div style={sectionStyle}>
              <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>Verify Signature</h3>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Algorithm:
                </label>
                <select
                  value={algorithm}
                  onChange={(e) => setAlgorithm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  <option value="HS256">HS256</option>
                  <option value="HS384">HS384</option>
                  <option value="HS512">HS512</option>
                  <option value="RS256">RS256</option>
                  <option value="RS384">RS384</option>
                  <option value="RS512">RS512</option>
                  <option value="PS256">PS256</option>
                  <option value="PS384">PS384</option>
                  <option value="PS512">PS512</option>
                  <option value="ES256">ES256</option>
                  <option value="ES384">ES384</option>
                  <option value="ES512">ES512</option>
                </select>
              </div>

              {algorithm.startsWith('HS') ? (
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Secret Key:
                  </label>
                  <textarea
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                    placeholder="Enter your secret key"
                    style={{
                      width: '100%',
                      height: '80px',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontFamily: 'Monaco, Consolas, monospace',
                      boxSizing: 'border-box',
                      resize: 'vertical'
                    }}
                  />
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Public Key (for verification):
                    </label>
                    <textarea
                      value={publicKey}
                      onChange={(e) => setPublicKey(e.target.value)}
                      placeholder="-----BEGIN PUBLIC KEY-----\n...\n-----END PUBLIC KEY-----"
                      style={{
                        width: '100%',
                        height: '120px',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontFamily: 'Monaco, Consolas, monospace',
                        boxSizing: 'border-box',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Private Key (for signing):
                    </label>
                    <textarea
                      value={privateKey}
                      onChange={(e) => setPrivateKey(e.target.value)}
                      placeholder="-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----"
                      style={{
                        width: '100%',
                        height: '120px',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontFamily: 'Monaco, Consolas, monospace',
                        boxSizing: 'border-box',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                </>
              )}

              {isValid !== null && (
                <div style={{
                  padding: '10px',
                  backgroundColor: isValid ? '#d4edda' : '#f8d7da',
                  color: isValid ? '#155724' : '#721c24',
                  border: `1px solid ${isValid ? '#c3e6cb' : '#f5c6cb'}`,
                  borderRadius: '4px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  {isValid ? '✓ Signature Verified' : '✗ Invalid Signature'}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Decoded JWT */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            {/* Header */}
            <div style={{...sectionStyle, height: headerMaximized ? 'auto' : '200px'}}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0, color: '#fb015b' }}>HEADER: ALGORITHM & TOKEN TYPE</h3>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <button 
                    onClick={() => setShowHeaderClaims(!showHeaderClaims)}
                    style={{
                      ...controlButtonStyle, 
                      backgroundColor: '#6c757d',
                      padding: '4px 8px',
                      fontSize: '12px',
                      width: 'auto'
                    }}
                  >
                    {showHeaderClaims ? 'JSON' : 'Claims'}
                  </button>
                  <button 
                    onClick={() => setHeaderMaximized(!headerMaximized)}
                    style={{
                      ...controlButtonStyle, 
                      backgroundColor: '#6c757d',
                      padding: '4px 8px',
                      fontSize: '12px',
                      width: 'auto'
                    }}
                  >
                    {headerMaximized ? '🗗' : '🗖'}
                  </button>
                  <button 
                    onClick={() => copyToClipboard(decodedHeader, 'header')} 
                    style={{
                      ...controlButtonStyle, 
                      backgroundColor: copySuccess === 'header' ? '#4CAF50' : '#fb015b',
                      padding: '4px 8px',
                      fontSize: '12px',
                      width: 'auto'
                    }}
                  >
                    {copySuccess === 'header' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
              
              {showHeaderClaims ? (
                <ClaimsTable claims={headerClaims} title="Header Claims" />
              ) : (
                <div style={{ position: 'relative', height: headerMaximized ? '400px' : '120px' }}>
                  <PreWithLines
                    value={decodedHeader}
                    preRef={headerRef}
                    style={{ 
                      backgroundColor: '#fff', 
                      margin: 0, 
                      height: '100%',
                      fontSize: '12px',
                      fontFamily: 'Monaco, Consolas, monospace'
                    }}
                  />
                </div>
              )}
            </div>

            {/* Payload */}
            <div style={{...sectionStyle, height: payloadMaximized ? 'auto' : '280px'}}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0, color: '#00b9f1' }}>PAYLOAD: DATA</h3>
                <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                  <select
                    value={payloadDisplayMode}
                    onChange={(e) => setPayloadDisplayMode(e.target.value)}
                    style={{
                      padding: '4px 8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: '#fff'
                    }}
                  >
                    <option value="raw">Raw JSON</option>
                    <option value="unescaped">JSON Unescaped</option>
                    <option value="claims">Claims Table</option>
                  </select>
                  <button 
                    onClick={() => setPayloadMaximized(!payloadMaximized)}
                    style={{
                      ...controlButtonStyle, 
                      backgroundColor: '#6c757d',
                      padding: '4px 8px',
                      fontSize: '12px',
                      width: 'auto'
                    }}
                  >
                    {payloadMaximized ? '🗗' : '🗖'}
                  </button>
                  <button 
                    onClick={() => copyToClipboard(
                      payloadDisplayMode === 'unescaped' ? unescapedPayload : decodedPayload, 
                      'payload'
                    )} 
                    style={{
                      ...controlButtonStyle, 
                      backgroundColor: copySuccess === 'payload' ? '#4CAF50' : '#00b9f1',
                      padding: '4px 8px',
                      fontSize: '12px',
                      width: 'auto'
                    }}
                  >
                    {copySuccess === 'payload' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
              
              {payloadDisplayMode === 'claims' ? (
                <ClaimsTable claims={payloadClaims} title="Payload Claims" />
              ) : (
                <div style={{ position: 'relative', height: payloadMaximized ? '400px' : '200px' }}>
                  <PreWithLines
                    value={payloadDisplayMode === 'unescaped' ? unescapedPayload : decodedPayload}
                    preRef={payloadRef}
                    style={{ 
                      backgroundColor: '#fff', 
                      margin: 0, 
                      height: '100%',
                      fontSize: '12px',
                      fontFamily: 'Monaco, Consolas, monospace'
                    }}
                  />
                </div>
              )}
            </div>

            {/* Signature */}
            <div style={{...sectionStyle, height: signatureMaximized ? 'auto' : '120px'}}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0, color: '#00d9ff' }}>VERIFY SIGNATURE</h3>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <button 
                    onClick={() => setSignatureMaximized(!signatureMaximized)}
                    style={{
                      ...controlButtonStyle, 
                      backgroundColor: '#6c757d',
                      padding: '4px 8px',
                      fontSize: '12px',
                      width: 'auto'
                    }}
                  >
                    {signatureMaximized ? '🗗' : '🗖'}
                  </button>
                  <button 
                    onClick={() => copyToClipboard(signature, 'signature')} 
                    style={{
                      ...controlButtonStyle, 
                      backgroundColor: copySuccess === 'signature' ? '#4CAF50' : '#00d9ff',
                      padding: '4px 8px',
                      fontSize: '12px',
                      width: 'auto'
                    }}
                  >
                    {copySuccess === 'signature' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
              
              <div style={{
                padding: '10px',
                backgroundColor: '#fff',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontFamily: 'Monaco, Consolas, monospace',
                fontSize: '12px',
                wordBreak: 'break-all',
                minHeight: signatureMaximized ? '80px' : '40px'
              }}>
                {signature}
              </div>
            </div>
          </div>
        </div>
        )}

        {activeTab === 'encoder' && (
          <div style={{ 
            display: 'flex', 
            gap: '20px', 
            minHeight: '600px',
            backgroundColor: '#fff',
            border: '2px solid #00BFA5',
            borderTop: 'none',
            borderRadius: '0 0 12px 12px',
            padding: '20px',
            boxShadow: '0 4px 12px rgba(0,191,165,0.1)'
          }}>
            {/* Left Panel - Header & Payload */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              {/* Header Section */}
              <div style={sectionStyle}>
                <h3 style={{ margin: '0 0 15px 0', color: '#fb015b' }}>HEADER</h3>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                    Algorithm:
                  </label>
                  <select
                    value={encoderAlgorithm}
                    onChange={(e) => setEncoderAlgorithm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      marginBottom: '10px'
                    }}
                  >
                    <option value="HS256">HS256</option>
                    <option value="HS384">HS384</option>
                    <option value="HS512">HS512</option>
                    <option value="RS256">RS256</option>
                    <option value="RS384">RS384</option>
                    <option value="RS512">RS512</option>
                  </select>
                </div>
                <textarea
                  value={encoderHeaderJson}
                  onChange={(e) => setEncoderHeaderJson(e.target.value)}
                  placeholder="Header JSON..."
                  style={{
                    width: '100%',
                    height: '120px',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: '12px',
                    lineHeight: '1.4',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Payload Section */}
              <div style={sectionStyle}>
                <h3 style={{ margin: '0 0 15px 0', color: '#00b9f1' }}>PAYLOAD</h3>
                <textarea
                  value={encoderPayloadJson}
                  onChange={(e) => setEncoderPayloadJson(e.target.value)}
                  placeholder="Payload JSON..."
                  style={{
                    width: '100%',
                    height: '200px',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: '12px',
                    lineHeight: '1.4',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Secret Section */}
              <div style={sectionStyle}>
                <h3 style={{ margin: '0 0 15px 0', color: '#00d9ff' }}>SECRET</h3>
                <textarea
                  value={encoderSecret}
                  onChange={(e) => setEncoderSecret(e.target.value)}
                  placeholder="Enter your secret key..."
                  style={{
                    width: '100%',
                    height: '80px',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: '12px',
                    lineHeight: '1.4',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* Right Panel - Generated JWT */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={sectionStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0, color: '#333' }}>GENERATED JWT TOKEN</h3>
                  <button 
                    onClick={() => copyToClipboard(editableToken || encodedJwtToken, 'encoded')} 
                    style={{
                      ...controlButtonStyle, 
                      backgroundColor: copySuccess === 'encoded' ? '#4CAF50' : '#00BFA5',
                      padding: '4px 8px',
                      fontSize: '12px',
                      width: 'auto'
                    }}
                  >
                    {copySuccess === 'encoded' ? 'Copied!' : 'Copy Token'}
                  </button>
                </div>
                
                <div style={{ marginBottom: '10px' }}>
                  <textarea
                    value={editableToken}
                    onChange={(e) => setEditableToken(e.target.value)}
                    onBlur={(e) => {
                      // Clean the token when user finishes editing (on blur)
                      const cleaned = e.target.value.replace(/\s+/g, '').trim();
                      if (cleaned !== e.target.value) {
                        setEditableToken(cleaned);
                      }
                    }}
                    placeholder="Generated JWT token will appear here. You can edit it manually..."
                    style={{
                      width: '100%',
                      height: '150px',
                      padding: '15px',
                      border: '2px solid #e9ecef',
                      borderRadius: '8px',
                      fontFamily: 'Monaco, Consolas, monospace',
                      fontSize: '12px',
                      lineHeight: '1.6',
                      resize: 'vertical',
                      boxSizing: 'border-box',
                      wordBreak: 'break-all'
                    }}
                  />
                </div>
                
                {editableToken && (
                  <div style={{ 
                    padding: '10px', 
                    backgroundColor: '#f8f9fa', 
                    border: '1px solid #e9ecef', 
                    borderRadius: '4px',
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: '11px',
                    wordBreak: 'break-all',
                    lineHeight: '1.4',
                    marginBottom: '10px'
                  }}>
                    <strong>Color Preview:</strong><br/>
                    {editableToken.split('.').length === 3 ? (
                      <span>
                        <span style={{ color: '#fb015b' }}>{editableToken.split('.')[0]}</span>
                        <span style={{ color: '#d63aff' }}>.</span>
                        <span style={{ color: '#00b9f1' }}>{editableToken.split('.')[1]}</span>
                        <span style={{ color: '#d63aff' }}>.</span>
                        <span style={{ color: '#00d9ff' }}>{editableToken.split('.')[2]}</span>
                      </span>
                    ) : (
                      <span style={{ color: '#dc3545' }}>Invalid JWT format</span>
                    )}
                  </div>
                )}

                {encoderError && (
                  <div style={{ 
                    padding: '10px', 
                    backgroundColor: '#fff3cd', 
                    color: '#856404',
                    border: '1px solid #ffeaa7',
                    borderRadius: '4px',
                    marginTop: '10px',
                    fontSize: '12px'
                  }}>
                    <strong>Note:</strong> {encoderError}
                  </div>
                )}

              </div>

              {/* Helpful Tips */}
              <div style={{...sectionStyle, backgroundColor: '#f0f8ff', border: '1px solid #b3d9ff'}}>
                <h4 style={{ margin: '0 0 10px 0', color: '#0066cc', fontSize: '16px' }}>💡 Encoder Tips</h4>
                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', lineHeight: '1.5' }}>
                  <li>Edit the Header and Payload JSON directly</li>
                  <li>The algorithm will automatically update in the header</li>
                  <li>Token is generated automatically as you type</li>
                  <li>Click "Verify in Debugger" to decode your token</li>
                  <li>Only HS256 signing is implemented in this demo</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Information Section */}
        <div style={{ marginTop: '40px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <h2 style={{ fontSize: '24px', marginBottom: '15px', color: '#333' }}>About JWT (JSON Web Tokens)</h2>
          <p style={{ marginBottom: '15px', lineHeight: '1.6', color: '#555' }}>
            JSON Web Token (JWT) is an open standard (RFC 7519) that defines a compact and self-contained way 
            for securely transmitting information between parties as a JSON object. This information can be 
            verified and trusted because it is digitally signed.
          </p>
          
          <h3 style={{ fontSize: '18px', marginBottom: '10px', color: '#333' }}>JWT Structure</h3>
          <ul style={{ marginBottom: '15px', paddingLeft: '20px', lineHeight: '1.6', color: '#555' }}>
            <li><strong style={{ color: '#fb015b' }}>Header:</strong> Contains the signing algorithm and token type</li>
            <li><strong style={{ color: '#00b9f1' }}>Payload:</strong> Contains the claims (statements about an entity)</li>
            <li><strong style={{ color: '#00d9ff' }}>Signature:</strong> Used to verify the sender and ensure the message wasn't changed</li>
          </ul>

          <h3 style={{ fontSize: '18px', marginBottom: '10px', color: '#333' }}>Common Use Cases</h3>
          <ul style={{ marginBottom: '15px', paddingLeft: '20px', lineHeight: '1.6', color: '#555' }}>
            <li><strong>Authorization:</strong> Single Sign On (SSO) widely uses JWT</li>
            <li><strong>Information Exchange:</strong> Securely transmitting information between parties</li>
            <li><strong>API Authentication:</strong> Stateless authentication for REST APIs</li>
          </ul>

          <div style={{ 
            padding: '15px', 
            backgroundColor: '#fff3cd', 
            borderLeft: '4px solid #ffc107',
            marginTop: '20px'
          }}>
            <strong>⚠️ Security Notice:</strong> Never include sensitive information in JWT payload as it's only base64 encoded, not encrypted. 
            Always verify signatures on the server side and use HTTPS in production.
          </div>
        </div>
      </div>
      
    </>
  );
};

export default JwtDecoderPage;
